"use client";
import { useRovingTabs } from "@/hooks/useRovingTabs.hook";
import {
  WhatsAppPhone,
  type WhatsAppChatHeader,
  type WhatsAppMessage,
} from "@/components/marketing";
import PillTabScroller, {
  pillTabClass,
} from "../pillTabScroller/pillTabScroller.component";
import { useId } from "react";

export interface WhatsAppExample {
  tab: string;
  message: WhatsAppMessage;
  // "Automatic, after the first visit" / "A campaign you create".
  when: string;
  note: string;
}

export interface WhatsAppExamplesData {
  ariaLabel: string;
  tablistLabel: string;
  chat: WhatsAppChatHeader;
  whenLabel: string;
  examples: WhatsAppExample[];
}

// "WhatsApp messages for a <shop>" - the preview's `wa_*` tab viewer, on
// the dark band. Pill tabs pick a message; the panel shows it in a chat
// mock-up beside when and how it is sent.
const WhatsAppExamples = ({
  ariaLabel,
  tablistLabel,
  chat,
  whenLabel,
  examples,
}: WhatsAppExamplesData) => {
  const tabs = useRovingTabs(examples.length);
  const baseId = useId();

  return (
    <figure role="group" aria-label={ariaLabel} className="m-0">
      <PillTabScroller ariaLabel={tablistLabel}>
        {examples.map((example, index) => {
          const isActive = index === tabs.activeIndex;
          return (
            <button
              key={example.tab}
              type="button"
              role="tab"
              id={`${baseId}-tab-${index}`}
              aria-controls={`${baseId}-panel-${index}`}
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => tabs.select(index)}
              onKeyDown={(event) => tabs.onTabKeyDown(event, index)}
              className={pillTabClass(isActive)}
            >
              {example.tab}
            </button>
          );
        })}
      </PillTabScroller>

      {examples.map((example, index) => (
        <div
          key={example.tab}
          role="tabpanel"
          id={`${baseId}-panel-${index}`}
          aria-labelledby={`${baseId}-tab-${index}`}
          hidden={index !== tabs.activeIndex}
          className="mx-auto mt-6 max-w-[820px]"
        >
          <div className="grid items-center gap-8 md:grid-cols-[272px_1fr] md:gap-[34px]">
            <WhatsAppPhone chat={chat} messages={[example.message]} />
            <div className="grid gap-2">
              <span className="text-[13px] font-bold text-white/75">{whenLabel}</span>
              <b className="text-[1.4rem] leading-tight font-bold text-white">
                {example.when}
              </b>
              <p className="text-base leading-relaxed text-white/80">{example.note}</p>
            </div>
          </div>
        </div>
      ))}
    </figure>
  );
};

export default WhatsAppExamples;
